import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    input,
    OnDestroy,
    OnInit,
    Output,
    signal
} from '@angular/core';
import {Subject, takeUntil} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {CommonModule} from '@angular/common';
import {CdkDrag, CdkDragDrop, CdkDragPlaceholder, CdkDragPreview, CdkDropList, CdkDropListGroup, moveItemInArray, transferArrayItem} from '@angular/cdk/drag-drop';
import {NgbModal, NgbTooltip} from '@ng-bootstrap/ng-bootstrap';
import {CampaignsApiService} from '@core/services/api/campaigns-api.service';
import {AlertService} from '@core/services/alert.service';
import {CampaignTaskStatus} from '@core/models/campaign.enums';
import {AppLoadingOverlayComponent} from '@shared/components/commons/app-loading-overlay/app-loading-overlay.component';
import {AppEmptyAlertMessageComponent} from '@shared/components/commons/app-empty-alert-message/app-empty-alert-message.component';
import {TaskDetailsModalComponent} from '@features/campaigns/components/task-details-modal/task-details-modal.component';
import {InitialsPipe} from '@shared/pipes/initials.pipe';
import {CpfCnpjPipe} from '@shared/pipes/app-cpfcnpj.pipe';
import {FormsModule} from '@angular/forms';
import {AppInputComponent} from '@shared/components/forms/app-input/app-input.component';
import {AppSelectComponent} from '@shared/components/forms/app-select/app-select.component';
import {CdkScrollable} from "@angular/cdk/overlay";
import {NgxMaskDirective} from 'ngx-mask';

const PAGE_SIZE = 20; // Items per page for infinite scroll

export interface KanbanCard {
    id: number;
    campaignId: number;
    companyName: string;
    documentNumber: string;
    estimatedDebtValue: number;
    position: number;
    city?: string;
    state?: string;
    assignee?: {
        id: number;
        firstName: string;
        lastName: string;
    };
}

export interface KanbanColumn {
    id: string;
    title: string;
    status: CampaignTaskStatus;
    cards: KanbanCard[];
    color: string;
    icon: string;
    // Pagination for infinite scroll
    page: number;
    hasMore: boolean;
    totalItems: number;
    totalDebtValue: number;
    isLoadingMore: boolean;
}

export interface KanbanConfig {
    // API endpoint configuration
    loadTasksFn: (params: any) => any; // Observable function to load tasks
    updateTaskPositionFn?: (campaignId: number, taskId: number, payload: any) => any; // Optional custom update

    // Optional features
    showAssigneeFilter?: boolean;
    showSearch?: boolean;
    campaignId?: number; // Campaign context

    // Users for assignee filter (if showAssigneeFilter is true)
    users?: any[];

    // Feature flags
    allowAssigneeManagement?: boolean;  // Allow assigning/unassigning users to tasks
    allowRemoveFromCampaign?: boolean;  // Allow removing tasks from campaign
}

@Component({
    selector: 'app-kanban-board',
    standalone: true,
    imports: [
        CommonModule,
        CdkDropListGroup,
        CdkDropList,
        CdkDrag,
        CdkDragPreview,
        CdkDragPlaceholder,
        AppLoadingOverlayComponent,
        AppEmptyAlertMessageComponent,
        InitialsPipe,
        CpfCnpjPipe,
        FormsModule,
        AppInputComponent,
        AppSelectComponent,
        NgbTooltip,
        CdkScrollable,
        NgxMaskDirective
    ],
    templateUrl: './kanban-board.component.html',
    styleUrl: './kanban-board.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KanbanBoardComponent implements OnInit, OnDestroy {
    private campaignsApiService = inject(CampaignsApiService);
    private alertService = inject(AlertService);
    private modalService = inject(NgbModal);

    // Inputs
    config = input.required<KanbanConfig>();
    campaignId = input<number>(); // For campaign context

    // Outputs
    @Output() tasksLoaded = new EventEmitter<number>(); // Total tasks loaded

    protected isLoading = signal(true);
    protected columns = signal<KanbanColumn[]>([]);

    // Filters
    protected searchTerm = signal<string>('');
    protected selectedAssignees = signal<number[]>([]);
    protected phone = signal<string>('');
    protected partnerName = signal<string>('');
    protected currentFilters = signal<any>({});

    private destroy$ = new Subject<void>();
    private filterDebounce = new Subject<void>();

    // Assignee dropdown
    protected openAssigneeDropdown = signal<number | null>(null);
    protected users = signal<any[]>([]);
    protected isLoadingUsers = signal(false);

    // Column definitions matching original campaign pipeline
    protected readonly columnDefinitions: Array<{
        id: string;
        title: string;
        status: CampaignTaskStatus;
        color: string;
        icon: string;
    }> = [
        {
            id: 'pending',
            title: 'Pendente',
            status: CampaignTaskStatus.PENDING,
            color: '#6366f1', // primary blue
            icon: 'ti-inbox'
        },
        {
            id: 'in_contact',
            title: 'Em Contato',
            status: CampaignTaskStatus.IN_CONTACT,
            color: '#0dcaf0', // info cyan
            icon: 'ti-phone'
        },
        {
            id: 'proposal_sent',
            title: 'Proposta Enviada',
            status: CampaignTaskStatus.PROPOSAL_SENT,
            color: '#ffc107', // warning yellow
            icon: 'ti-mail'
        },
        {
            id: 'under_negotiation',
            title: 'Em Negociação',
            status: CampaignTaskStatus.UNDER_NEGOTIATION,
            color: '#6c757d', // secondary gray
            icon: 'ti-message-circle'
        },
        {
            id: 'closed',
            title: 'Fechadas',
            status: CampaignTaskStatus.CLOSED,
            color: '#198754', // success green
            icon: 'ti-check'
        },
        {
            id: 'lost',
            title: 'Perdidas',
            status: CampaignTaskStatus.LOST,
            color: '#dc3545', // danger red
            icon: 'ti-x'
        }
    ];

    ngOnInit(): void {
        this.initializeColumns();
        this.loadAllColumns();
        this.loadUsers();

        this.filterDebounce.pipe(
            debounceTime(500),
            takeUntil(this.destroy$)
        ).subscribe(() => this.applyFilters());
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadUsers(): void {
        const configUsers = this.config().users;
        if (configUsers && configUsers.length > 0) {
            this.users.set(configUsers);
        } else if (this.config().showAssigneeFilter) {
            // Load users from API if not provided and assignee filter is enabled
            this.isLoadingUsers.set(true);
            this.campaignsApiService.getCampaignUsers().subscribe({
                next: (response: any) => {
                    if (response.success && response.data) {
                        this.users.set(response.data);
                    }
                    this.isLoadingUsers.set(false);
                },
                error: () => {
                    this.isLoadingUsers.set(false);
                }
            });
        }
    }

    private initializeColumns(): void {
        this.columns.set(
            this.columnDefinitions.map(def => ({
                ...def,
                cards: [],
                page: 0,
                hasMore: true,
                totalItems: 0,
                totalDebtValue: 0,
                isLoadingMore: false
            }))
        );
    }

    loadAllColumns(): void {
        this.isLoading.set(true);

        const loadPromises = this.columnDefinitions.map(columnDef =>
            this.loadColumnByStatus(columnDef.status)
        );

        Promise.all(loadPromises).finally(() => {
            this.isLoading.set(false);
            this.emitTotalTasks();
        });
    }

    private loadColumnByStatus(status: CampaignTaskStatus): Promise<void> {
        return new Promise((resolve) => {
            const params: any = {
                status,
                page: 0,
                pageSize: PAGE_SIZE
            };

            // Add filters if present
            const filters = this.currentFilters();
            if (filters.q) params.q = filters.q;
            if (filters.phone) params.phone = filters.phone;
            if (filters.partnerName) params.partnerName = filters.partnerName;
            if (filters.assignedTo && filters.assignedTo.length > 0) {
                params.assignedTo = filters.assignedTo;
            }

            this.config().loadTasksFn(params).subscribe({
                next: (response: any) => {
                    if (response.success && response.data) {
                        // Backend returns data.result array
                        const tasks = response.data.result || [];
                        const statistics = response.data.statistics || {};
                        const totalCount = statistics.totalCount || 0;
                        const totalDebtValue = statistics.totalDebtValue || 0;

                        this.updateColumn(status, tasks, {
                            page: 0,
                            totalItems: totalCount,
                            totalDebtValue: totalDebtValue,
                            hasMore: response.data.currentPage < response.data.totalPages
                        });
                    }
                    resolve();
                },
                error: () => {
                    resolve();
                }
            });
        });
    }

    private updateColumn(status: CampaignTaskStatus, tasks: any, metadata?: Partial<KanbanColumn>): void {
        const updatedColumns = this.columns().map(column => {
            if (column.status === status) {
                const tasksArray = Array.isArray(tasks) ? tasks : [];
                const cards: KanbanCard[] = tasksArray.map((task: any) => ({
                    id: task.id,
                    campaignId: task.campaignId,
                    companyName: task.company?.name || '',
                    documentNumber: task.company?.documentNumber || '',
                    estimatedDebtValue: task.estimatedDebtValue || 0,
                    position: task.displayOrder || 0,
                    city: task.company?.city,
                    state: task.company?.state,
                    assignee: task.assignee ? {
                        id: task.assignee.id,
                        firstName: task.assignee.firstName,
                        lastName: task.assignee.lastName
                    } : undefined
                }));
                return {...column, cards, ...metadata};
            }
            return column;
        });

        this.columns.set(updatedColumns);
    }

    private emitTotalTasks(): void {
        const total = this.getTotalCards();
        this.tasksLoaded.emit(total);
    }

    protected onDrop(event: CdkDragDrop<KanbanCard[]>, targetColumn: KanbanColumn): void {
        const card = event.item.data as KanbanCard;

        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
            this.updateCardPosition(card, targetColumn, event.currentIndex);
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex
            );
            this.updateCardStatusAndPosition(card, targetColumn, event.currentIndex);
        }
    }

    private updateCardPosition(card: KanbanCard, column: KanbanColumn, newIndex: number): void {
        const cards = column.cards;
        const afterTaskId = newIndex > 0 ? cards[newIndex - 1].id : null;
        const beforeTaskId = newIndex < cards.length - 1 ? cards[newIndex + 1].id : null;

        this.campaignsApiService.updateTaskPosition(card.campaignId, card.id, {
            newStatus: column.status,
            afterTaskId,
            beforeTaskId
        }).subscribe({
            next: () => {
                // Position updated successfully
            },
            error: () => {
                this.alertService.error('Erro', 'Não foi possível atualizar a posição da tarefa');
                this.loadAllColumns();
            }
        });
    }

    private updateCardStatusAndPosition(card: KanbanCard, targetColumn: KanbanColumn, newIndex: number): void {
        const cards = targetColumn.cards;
        const afterTaskId = newIndex > 0 ? cards[newIndex - 1].id : null;
        const beforeTaskId = newIndex < cards.length - 1 ? cards[newIndex + 1].id : null;

        this.campaignsApiService.updateTaskPosition(card.campaignId, card.id, {
            newStatus: targetColumn.status,
            afterTaskId,
            beforeTaskId
        }).subscribe({
            next: () => {
                this.loadAllColumns();
            },
            error: () => {
                this.alertService.error('Erro', 'Não foi possível mover a tarefa');
                this.loadAllColumns();
            }
        });
    }

    protected openTaskDetails(card: KanbanCard): void {
        const modalRef = this.modalService.open(TaskDetailsModalComponent, {
            size: 'xl',
            centered: true,
            backdrop: 'static'
        });

        modalRef.componentInstance.campaignId = card.campaignId;
        modalRef.componentInstance.taskId = card.id;
        modalRef.componentInstance.allowRemoveFromCampaign = this.config().allowRemoveFromCampaign;
        modalRef.componentInstance.allowAssigneeManagement = this.config().allowAssigneeManagement;

        modalRef.result.then(
            (result) => {
                this.loadAllColumns();
            },
            () => {
                // Modal dismissed
            }
        );
    }

    protected getTotalCards(): number {
        // Use statistics total instead of loaded cards
        return this.columns().reduce((sum, col) => sum + col.totalItems, 0);
    }

    protected getColumnTotal(column: KanbanColumn): number {
        return column.cards.reduce((sum, card) => sum + (card.estimatedDebtValue || 0), 0);
    }

    protected onSearchChange(term: string): void {
        this.searchTerm.set(term);
        this.applyFilters();
    }

    protected onAssigneeFilterChange(assigneeIds: number[]): void {
        this.selectedAssignees.set(assigneeIds);
        this.applyFilters();
    }

    protected onPhoneChange(value: string): void {
        this.phone.set(value);
        this.filterDebounce.next();
    }

    protected onPartnerNameChange(value: string): void {
        this.partnerName.set(value);
        this.applyFilters();
    }

    private applyFilters(): void {
        const filters: any = {};

        if (this.searchTerm()) filters.q = this.searchTerm();
        if (this.phone()) filters.phone = this.phone();
        if (this.partnerName()) filters.partnerName = this.partnerName();
        if (this.selectedAssignees().length > 0) filters.assignedTo = this.selectedAssignees();

        this.currentFilters.set(filters);
        this.loadAllColumns();
    }

    // Infinite scroll methods
    protected onColumnScroll(event: any, columnStatus: CampaignTaskStatus): void {
        const column = this.columns().find(c => c.status === columnStatus);

        // Don't trigger if already loading or no more items
        if (!column || column.isLoadingMore || !column.hasMore) {
            return;
        }

        const element = event.target;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;

        // Trigger when user is 100px from bottom
        const threshold = 100;
        if (scrollHeight - scrollTop - clientHeight < threshold) {
            this.loadMoreItems(columnStatus);
        }
    }

    private loadMoreItems(columnStatus: CampaignTaskStatus): void {
        const column = this.columns().find(c => c.status === columnStatus);
        if (!column || column.isLoadingMore || !column.hasMore) {
            return;
        }

        // Set loading state
        const updatedColumns = this.columns().map(c =>
            c.status === columnStatus ? {...c, isLoadingMore: true} : c
        );
        this.columns.set(updatedColumns);

        const nextPage = column.page + 1;
        const filters = this.currentFilters();

        const params: any = {
            status: columnStatus,
            page: nextPage,
            pageSize: PAGE_SIZE
        };

        if (filters.q) params.q = filters.q;
        if (filters.phone) params.phone = filters.phone;
        if (filters.partnerName) params.partnerName = filters.partnerName;
        if (filters.assignedTo && filters.assignedTo.length > 0) {
            params.assignedTo = filters.assignedTo;
        }

        this.config().loadTasksFn(params).subscribe({
            next: (response: any) => {
                if (response.success && response.data) {
                    const tasks = response.data.result || [];
                    const newCards: KanbanCard[] = tasks.map((task: any) => ({
                        id: task.id,
                        campaignId: task.campaignId,
                        companyName: task.company?.name || '',
                        documentNumber: task.company?.documentNumber || '',
                        estimatedDebtValue: task.estimatedDebtValue || 0,
                        position: task.displayOrder || 0,
                        city: task.company?.city,
                        state: task.company?.state,
                        assignee: task.assignee ? {
                            id: task.assignee.id,
                            firstName: task.assignee.firstName,
                            lastName: task.assignee.lastName
                        } : undefined
                    }));

                    const updatedColumns = this.columns().map(c => {
                        if (c.status === columnStatus) {
                            return {
                                ...c,
                                cards: [...c.cards, ...newCards],
                                page: nextPage,
                                hasMore: response.data.currentPage < response.data.totalPages,
                                isLoadingMore: false
                            };
                        }
                        return c;
                    });
                    this.columns.set(updatedColumns);
                }
            },
            error: () => {
                this.alertService.error('Erro', 'Falha ao carregar mais itens');
                const updatedColumns = this.columns().map(c =>
                    c.status === columnStatus ? {...c, isLoadingMore: false} : c
                );
                this.columns.set(updatedColumns);
            }
        });
    }

    // Assignee dropdown methods
    protected toggleAssigneeDropdown(cardId: number, event: Event): void {
        event.stopPropagation();
        const currentOpen = this.openAssigneeDropdown();
        this.openAssigneeDropdown.set(currentOpen === cardId ? null : cardId);
    }

    protected closeAssigneeDropdown(): void {
        this.openAssigneeDropdown.set(null);
    }

    protected assignUserToCard(card: KanbanCard, user: any, event: Event): void {
        event.stopPropagation();
        this.closeAssigneeDropdown();

        this.campaignsApiService.updateTaskAssignee(card.campaignId, card.id, user.id).subscribe({
            next: (response: any) => {
                if (response.success) {
                    // Update card assignee locally
                    const updatedColumns = this.columns().map(col => ({
                        ...col,
                        cards: col.cards.map(c =>
                            c.id === card.id
                                ? {...c, assignee: {id: user.id, firstName: user.firstName, lastName: user.lastName}}
                                : c
                        )
                    }));
                    this.columns.set(updatedColumns);
                    this.alertService.success('Sucesso', 'Vendedor atribuído');
                }
            },
            error: () => {
                this.alertService.error('Erro', 'Falha ao atribuir vendedor');
            }
        });
    }

    protected unassignUser(card: KanbanCard, event: Event): void {
        event.stopPropagation();
        this.closeAssigneeDropdown();

        this.campaignsApiService.updateTaskAssignee(card.campaignId, card.id, null).subscribe({
            next: (response: any) => {
                if (response.success) {
                    // Update card assignee locally
                    const updatedColumns = this.columns().map(col => ({
                        ...col,
                        cards: col.cards.map(c =>
                            c.id === card.id
                                ? {...c, assignee: undefined}
                                : c
                        )
                    }));
                    this.columns.set(updatedColumns);
                    this.alertService.success('Sucesso', 'Vendedor removido');
                }
            },
            error: () => {
                this.alertService.error('Erro', 'Falha ao remover vendedor');
            }
        });
    }

    // Helper to get Bootstrap color class from hex color
    protected getColumnColorClass(hexColor: string): string {
        const colorMap: {[key: string]: string} = {
            '#6366f1': 'col-primary',
            '#0dcaf0': 'col-info',
            '#ffc107': 'col-warning',
            '#6c757d': 'col-secondary',
            '#198754': 'col-success',
            '#dc3545': 'col-danger'
        };
        return colorMap[hexColor] || 'col-primary';
    }
}
